import { map, first } from 'underscore';
import React, { useContext, useMemo, useCallback, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  ServerValidatedRemainingCharsValidatedTextField,
  GroupInfoImg,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withFinalUrlRecommendationUpdate, } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withStoreFromProviderId, withStoreUrl } from '@bingads-webui-campaign-react/with-store-details';
import { ValidatedFinalUrlWithRecommendations } from '@bingads-webui-campaign-react/pmax-text-input';
import { entityIs, andComp, propertyIs } from '@bingads-webui-campaign-react/form-error';
import {
  ValidatedFixedPanel,
  Collapse,
  TextTitle,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { Text, Stack } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';

import { ERROR_ENTITY } from '../../../shared/model/api-error';
import { SocialSiteRecommendationSection } from './social-site-recommendation-section';
import { readSocialSiteRecommendation } from '../../../shared/model/social-site-recommendation';

const entityIsAssetGroup = entityIs(ERROR_ENTITY.AssetGroup);
const assetGroupNameProp = andComp(entityIsAssetGroup, propertyIs('Name'));
const assetGroupFinalUrlsProp = andComp(entityIsAssetGroup, propertyIs('FinalUrls'));

const ValidatedFinalUrlWithStoreURL = withError(withCustomValidation(withFinalUrlRecommendationUpdate(withStoreFromProviderId(withStoreUrl(ValidatedFinalUrlWithRecommendations)))));
const ServerValidatedFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedFinalUrlWithStoreURL, { filter: assetGroupFinalUrlsProp }));

export const AssetGroupNameSectionWithSocialSiteRecommendation = ({
  FinalUrls,
  permissions,
  onFinalURLFocusOut: prefillAssetRecommendation,
  campaign,
}) => {
  const i18n = useContext(I18nContext);

  const validateSpecialCharacters = permissions && permissions.dynamic && permissions.dynamic.PerformanceMaxCampaignsFinalURLValidation && permissions.dynamic.PMaxAndDSAFinalURLValidation;

  const showFinalUrlExpansion = campaign && !campaign.finalUrlExpansionOptOut;
  const providerId = campaign && campaign.ProviderId;
  const campaignId = campaign && campaign.Id;

  const { scope } = useContext(ScopeContext) || {};
  const { aid, cid } = scope.values;

  const [socialSites, setSocialSites] = useState([]);
  const [isSocialSitesLoading, setIsSocialSitesLoading] = useState(false);

  const onFinalURLFocusOut = useCallback(() => {
    setIsSocialSitesLoading(true);
    readSocialSiteRecommendation(cid, aid, campaignId, first(FinalUrls))
      .then(({ Data }) => {
        setIsSocialSitesLoading(false);
        if (Data && Data.SocialSites) {
          const socialSitesRecommendations = Data.SocialSites;
          const additionalSocialSites = map(socialSitesRecommendations, socialSite => ({
            ...socialSite,
            checked: true,
          }));
          setSocialSites([{ Url: first(FinalUrls), checked: true }].concat(additionalSocialSites));
        } else {
          setSocialSites([{ Url: first(FinalUrls), checked: true }]);
        }
      });
  }, [cid, aid, campaignId, FinalUrls]);

  const onSocialSiteCheckboxChange = useCallback((index, checked) => {
    socialSites[index].checked = checked;
    setSocialSites([...socialSites]);
  }, [socialSites]);

  const onRenderAdGroupNameLabel = useCallback(() => (
    <TextTitle titleKey={_TL_('Asset group name')} topicKey="pop_BA_PMAX_AssetGroupName" />
  ), []);

  const onRenderDescription = useMemo(() => (
    <Stack styles={{ root: { fontSize: 14, marginTop: 10, alignItems: 'center' } }} horizontal>
      <Text variant="small" >
        {i18n.getString(_TL_('URL expansion is on'))}
      </Text>
      <HelpPopup
        topic="pop_BA_PMAX_FinalURLExpansion"
        aria-label={i18n.getString(_TL_('Click to learn more about Final URL expansion'))}
      />
    </Stack>
  ), [i18n]);

  const finalUrlValidationProps = useMemo(() => ({
    data: {
      FinalUrls,
    },
    initialValue: {
      FinalUrls,
    },
    debounce: 700,
  }), [FinalUrls]);

  return (
    <div className={classNames('asset-group-name-section')}>
      <Collapse allPanelItems={['asset-group-info']} checkItems skipItemsTypeCheck activeKeys={['asset-group-info']}>
        <ValidatedFixedPanel
          key="asset-group-info"
          title={<TextTitle
            titleKey={_TL_('Asset group information')}
            icon={GroupInfoImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          <ServerValidatedRemainingCharsValidatedTextField
            placeholder={i18n.getString(_TL_('Enter your asset group name'))}
            id="input-asset-group-name"
            validationProps={{
                  field: 'Name',
                  schema: AssetGroup,
                }}
            i18n={i18n}
            label={i18n.getString(_TL_('Asset group name'))}
            onRenderLabel={onRenderAdGroupNameLabel}
            formErrorProps={{ filter: assetGroupNameProp }}
          />
          <ServerValidatedFinalUrl
            i18n={i18n}
            validationProps={finalUrlValidationProps}
            permissions={permissions}
            providerId={providerId}
            validateOnRender
            additionalProperties={{ onBlur: onFinalURLFocusOut }}
            enableSpecialCharactersValidation={validateSpecialCharacters}
          />
          {showFinalUrlExpansion && onRenderDescription}
          <SocialSiteRecommendationSection
            i18n={i18n}
            isSocialSitesLoading={isSocialSitesLoading}
            FinalUrls={FinalUrls}
            socialSites={socialSites}
            onSocialSiteCheckboxChange={onSocialSiteCheckboxChange}
            prefillAssetRecommendation={prefillAssetRecommendation}
          />
        </ValidatedFixedPanel>
      </Collapse>
    </div>
  );
};

AssetGroupNameSectionWithSocialSiteRecommendation.propTypes = {
  FinalUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      PerformanceMaxCampaignsFinalURLValidation: PropTypes.bool,
      PMaxAndDSAFinalURLValidation: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  onFinalURLFocusOut: PropTypes.func.isRequired,
  campaign: PropTypes.shape({
    finalUrlExpansionOptOut: PropTypes.bool,
    ProviderId: PropTypes.number,
    Id: PropTypes.number,
  }).isRequired,
};
